import { styled } from "@mui/material/styles";
import { ProgramTag } from "@portal/app/_common/types/program";
import { constantCase } from "@portal/app/_common/lib/changeCase";
import Typography from "@mui/material/Typography";
import { memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { faDiploma } from "@fortawesome/pro-solid-svg-icons/faDiploma";
import { faFire } from "@fortawesome/pro-solid-svg-icons/faFire";
import { faCalendarHeart } from "@fortawesome/pro-solid-svg-icons/faCalendarHeart";
import { faMoneyBill } from "@fortawesome/pro-solid-svg-icons/faMoneyBill";
import { faMessages } from "@fortawesome/pro-solid-svg-icons/faMessages";
import { faBrain } from "@fortawesome/pro-solid-svg-icons/faBrain";
import { Property } from "csstype";
import { faGraduationCap } from "@fortawesome/pro-solid-svg-icons/faGraduationCap";
import { faCross } from "@fortawesome/pro-solid-svg-icons/faCross";
import { faHandHoldingHeart } from "@fortawesome/pro-solid-svg-icons/faHandHoldingHeart";
import { faSwapArrows } from "@fortawesome/pro-solid-svg-icons/faSwapArrows";

const LABEL_STYLE_MAP: Record<
  ProgramTag,
  { background: Property.BackgroundColor; icon: IconProp } | null
> = {
  TOP_RATED: {
    background: "#ebaad3",
    icon: faStar,
  },
  STUDENT_FAVORITE: {
    background: "#f9b0c7",
    icon: faFire,
  },
  ACCELERATED: {
    background: "#f8c7c0",
    icon: faDiploma,
  },
  FLEXIBLE_SCHEDULE: {
    background: "#f6ddb7",
    icon: faCalendarHeart,
  },
  SELF_PACED: {
    background: "#f2f3ac",
    icon: faBrain,
  },
  BUDGET_FRIENDLY: {
    background: "#e4f9b3",
    icon: faMoneyBill,
  },
  QUICK_COMMUNICATION: {
    background: "#d1f9c1",
    icon: faMessages,
  },
  NONPROFIT: {
    background: "#b8f8f1",
    icon: faHandHoldingHeart,
  },
  CAN_TRANSFER_CREDITS: {
    background: "#b8df88",
    icon: faSwapArrows,
  },
  DEGREE_COMPLETION: {
    background: "#d1b8f8",
    icon: faGraduationCap,
  },
  CHRISTIAN_UNIVERSITY: {
    background: "#b8c0f8",
    icon: faCross,
  },
  ACCREDITED: null,
  NOT_ACCREDITED: null,
};

const height = 26;
export const DreamSearchResultLabel = memo(({ label }: { label: ProgramTag }) => {
  const title = constantCase(label).replaceAll("_", " ");
  const labelStyles = LABEL_STYLE_MAP[label];

  return labelStyles ? (
    <LabelContainer>
      <Label $height={height} $labelColor={labelStyles.background}>
        <FontAwesomeIcon icon={labelStyles.icon} size="xs" />
        {/* Decorative card badge, not a section heading — `subtitle2` would otherwise render an <h6>. */}
        <Typography variant="subtitle2" component="span" sx={{ pl: 1, lineHeight: 1 }}>
          {title}
        </Typography>
        <CSSFlag $labelColor={labelStyles.background} $height={height} className="label-flag" />
      </Label>
    </LabelContainer>
  ) : null;
});

const LabelContainer = styled("div")`
  display: flex;
  flex: 1;
  align-items: end;
`;

const Label = styled("div", {
  shouldForwardProp: (prop) => prop !== "$height" && prop !== "$labelColor",
})<{ $height: number; $labelColor: string }>`
  height: ${({ $height }) => `${$height}px`};
  background: ${({ $labelColor }) => $labelColor};
  color: black;
  position: relative;
  align-items: center;
  display: flex;
  margin-left: -1rem;
  padding-left: 1rem;
  padding-right: 1.3rem;
`;

const CSSFlag = styled("div", {
  shouldForwardProp: (prop) => prop !== "$height" && prop !== "$labelColor",
})<{ $height: number; $labelColor: string }>`
  background: ${({ $labelColor }) => $labelColor};
  position: absolute;
  right: 0;
  top: 0;
  border-top: ${({ $labelColor, $height }) => `${$height / 2}px solid ${$labelColor}`};
  border-bottom: ${({ $labelColor, $height }) => `${$height / 2}px solid ${$labelColor}`};
  border-right: ${({ $height }) => `${$height / 3}px solid #fff`};
`;
